import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';

const CheckBoxTimesheet = ({ checked, indeterminate, onChange, visible }) => {
  if (!visible) {
    return null;
  }

  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.checked, event);
    }
  };

  return (
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      onChange={handleChange}
    />
  );
};

export const useTimesheetCheckboxSelection = ({ destinations = [], missionTimesheetState }) => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectionInitialized, setSelectionInitialized] = useState(false);

  const rowKeyObj = useCallback((dest) => ({
    mission_destination_id: dest?.mission_destination_id ?? null,
    mission_timesheet_destination_id: dest?.mission_timesheet_destination_id ?? null,
  }), []);

  const isSameKey = useCallback(
    (a, b) =>
      (a?.mission_destination_id ?? null) === (b?.mission_destination_id ?? null) &&
      (a?.mission_timesheet_destination_id ?? null) === (b?.mission_timesheet_destination_id ?? null) &&
      (a?.mission_timesheet_destination_id ?? null) === (b?.mission_timesheet_destination_id ?? null),
    [],
  );

  const includesKey = useCallback((arr, key) => (arr || []).some((k) => isSameKey(k, key)), [isSameKey]); // clé présente dans un tableau

  const canSelectDestination = useCallback(
    (d) => // peut-on selectionner la destination
      !['CONFIRMED', 'REFUSED'].includes(missionTimesheetState) &&
      !["AVION", "BATEAU"].includes(d?.transport_type),
    [missionTimesheetState],
  );

  const computeSelectableKeys = useCallback(
    (items = destinations) => {
      if (!items?.length) {
        return [];
      }
      return items.reduce((acc, destination) => {
        if (canSelectDestination(destination)) {
          acc.push(rowKeyObj(destination));
        }
        return acc;
      }, []);
    },
    [destinations, canSelectDestination, rowKeyObj],
  );

  const findDestinationIndexByKey = useCallback(
    (key) => destinations.findIndex((dest) => isSameKey(rowKeyObj(dest), key)),
    [destinations, isSameKey, rowKeyObj],
  );

  const handleToggleAll = useCallback(
    (checked) => {
      const keys = computeSelectableKeys();
      setSelectedKeys(checked ? keys : []);
    },
    [computeSelectableKeys],
  );

  useEffect(() => {
    if (!destinations.length) {
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys((prev = []) =>
      prev.filter((key) => {
        const index = findDestinationIndexByKey(key);
        return index !== -1 && canSelectDestination(destinations[index]);
      }),
    );
  }, [destinations, findDestinationIndexByKey, canSelectDestination]);

  useEffect(() => {
    if (selectionInitialized) {
      return;
    }
    if (!destinations.length) {
      return;
    }
    const autoKeys = computeSelectableKeys(destinations);
    setSelectedKeys(autoKeys);
    setSelectionInitialized(true);
  }, [destinations, selectionInitialized, computeSelectableKeys]);

  const handleToggleOne = useCallback(
    (keyObj, checked) => {
      setSelectedKeys((prev = []) => {
        if (checked) {
          return includesKey(prev, keyObj) ? prev : [...prev, keyObj];
        }
        return prev.filter((k) => !isSameKey(k, keyObj));
      });
    },
    [includesKey, isSameKey],
  );

  const selectableKeys = computeSelectableKeys();
  const totalSelectable = selectableKeys.length;
  const allowSelectionUi = !['CONFIRMED', 'REFUSED'].includes(missionTimesheetState);
  const showSelectAll = destinations.length > 0 && totalSelectable === destinations.length;
  const allSelectableChecked = totalSelectable > 0 && selectableKeys.every((key) => includesKey(selectedKeys, key));
  const partiallyChecked =
    totalSelectable > 0 && selectableKeys.some((key) => includesKey(selectedKeys, key)) && !allSelectableChecked;

  const getHeaderCheckboxProps = useCallback(
    () => ({
      visible: allowSelectionUi && showSelectAll,
      checked: allSelectableChecked,
      indeterminate: partiallyChecked,
      onChange: handleToggleAll,
    }),
    [allowSelectionUi, showSelectAll, allSelectableChecked, partiallyChecked, handleToggleAll],
  );

  const getRowCheckboxProps = useCallback(
    (destination) => {
      const keyObj = rowKeyObj(destination);
      return {
        visible: allowSelectionUi && canSelectDestination(destination),
        checked: includesKey(selectedKeys, keyObj),
        onChange: (checked) => handleToggleOne(keyObj, checked),
      };
    },
    [allowSelectionUi, canSelectDestination, includesKey, selectedKeys, rowKeyObj, handleToggleOne],
  );

  const buildSelectedDestinationDetails = useCallback(
    (keys = []) => {
      if (!Array.isArray(keys) || !keys.length) {
        return [];
      }
      return keys.reduce((acc, key) => {
        const index = findDestinationIndexByKey(key);
        if (index === -1) {
          return acc;
        }
        const destination = destinations[index];
        if (!destination) {
          return acc;
        }
        acc.push({
          mission_destination_id: destination?.mission_destination_id ?? null,
          mission_timesheet_destination_id: destination?.mission_timesheet_destination_id ?? null,
          transport_type: destination?.transport_type || 'FID',
          mission_rent_id: destination?.mission_rent_id ?? null,
          transports: destination?.transports || [],
          date_begin: destination?.date_begin || null,
          date_end: destination?.date_end || null,
          missionnaries: destination?.missionnaries || [],
        });
        return acc;
      }, []);
    },
    [destinations, findDestinationIndexByKey],
  );

  const resetSelection = useCallback(() => {
    setSelectedKeys([]);
    setSelectionInitialized(false);
  }, []);

  return {
    selectedKeys,
    resetSelection,
    buildSelectedDestinationDetails,
    getHeaderCheckboxProps,
    getRowCheckboxProps,
  };
};

export default CheckBoxTimesheet;
